import type { GameState } from '../state/types';
import { beenInFlag, NOSY_HOMES } from './parkHomes';

export interface AchievementDef {
  id: string;
  title: string;
  /** Her title, where it's different (Mud Bog Queen). */
  titleW?: string;
  /** Completes the sentence "You might be a redneck if..." */
  line: string;
  /** Automatic unlock condition. Achievements without one are unlocked by game events. */
  check?: (s: GameState) => boolean;
}

const c = (s: GameState, key: keyof GameState['counters']): number => s.counters[key] ?? 0;

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  {
    id: 'first_beer',
    title: 'Crack a Cold One',
    line: '...you consider light beer a food group.',
    check: (s) => c(s, 'beers') >= 1,
  },
  {
    id: 'beers_50',
    title: 'Liver of Steel',
    line: '...your recycling bin is the heaviest thing you own.',
    check: (s) => c(s, 'beers') >= 50,
  },
  {
    id: 'sport_mode',
    title: 'Sport Mode Engaged',
    line: '...you have ever flipped your Crocs into sport mode to chase a dog.',
    check: (s) => c(s, 'sprints') >= 1,
  },
  {
    id: 'cans_100',
    title: 'Aluminum Tycoon',
    line: '...you know the price of scrap aluminum but not your blood type.',
    check: (s) => c(s, 'cansRecycled') >= 100,
  },
  {
    id: 'scratch_win',
    title: 'Retirement Plan',
    line: '...your 401(k) is a stack of scratch-offs held together with a hair tie.',
    check: (s) => c(s, 'scratchWins') >= 1,
  },
  {
    id: 'scratch_big',
    title: 'Jackpot Jesus',
    line: '...you have thanked the Lord and the gas station clerk in the same breath.',
    check: (s) => c(s, 'bigWins') >= 1,
  },
  {
    id: 'hold_my_beer',
    title: 'Hold My Beer',
    line: '...your last words will most likely be "hold my beer, watch this."',
    check: (s) => c(s, 'stuntFails') >= 1,
  },
  {
    id: 'viral',
    title: 'Gone Viral',
    line: '...more strangers have seen you fall off a trampoline than have seen your face.',
  },
  {
    id: 'sushi',
    title: 'Living Dangerously',
    line: '...you have trusted a place that sells motor oil to sell you raw fish.',
    check: (s) => c(s, 'sushi') >= 1,
  },
  {
    id: 'tornado',
    title: 'Tornado Magnet',
    line: '...a tornado has visited your home more often than your relatives.',
    check: (s) => c(s, 'tornadoes') >= 1,
  },
  {
    id: 'possum',
    title: 'Possum Daddy',
    titleW: 'Possum Mama',
    line: '...your emotional support animal plays dead when stressed. Same, Kevin.',
    check: (s) => s.decor.includes('possum'),
  },
  {
    id: 'no_snitch',
    title: "I Ain't Seen Nothin'",
    line: '...you have told a deputy "I ain\'t seen nothin\'" and meant it spiritually.',
    check: (s) => c(s, 'noSnitch') >= 1,
  },
  {
    id: 'daytime_fireworks',
    title: "Freedom Don't Wait for Dark",
    line: '...you have lit fireworks at 2 PM on a Tuesday because you could.',
    check: (s) => c(s, 'daytimeFireworks') >= 1,
  },
  {
    id: 'camaro',
    title: 'She Runs!',
    line: '...your car was on blocks so long the county listed it as a landmark.',
    check: (s) => s.camaro >= 100,
  },
  {
    id: 'decor_5',
    title: 'Curb Appeal',
    line: '...you own more lawn ornaments than forks.',
    check: (s) => s.decor.length >= 5,
  },
  {
    id: 'neighborly',
    title: 'Neighborly',
    line: '...everybody in the park has borrowed something from you and returned nothing.',
    check: (s) =>
      ['darlene', 'earl', 'tammy', 'dale', 'jenkins', 'skeeter'].every((id) => s.flags[`met_${id}`]),
  },
  {
    id: 'pawned',
    title: 'Family Heirloom',
    line: "...you have pawned your grandpa's watch and called it 'liquidity.'",
    check: (s) => c(s, 'pawned') >= 1,
  },
  {
    id: 'payday',
    title: 'Financial Genius',
    line: '...your bank is a pawn shop and your financial advisor is named Lucky.',
    check: (s) => c(s, 'loans') >= 1,
  },
  {
    id: 'mlm',
    title: 'Boss Babe',
    line: '...you have joined a pyramid scheme because Tammy said "hun" three times.',
    check: (s) => c(s, 'mlm') >= 1,
  },
  {
    id: 'passed_out',
    title: 'Woke Up Like This',
    line: '...you have woken up somewhere unexpected and called it an adventure.',
    check: (s) => c(s, 'passOuts') >= 1,
  },
  {
    id: 'murray',
    title: 'You Are NOT the Father',
    line: '...daytime paternity TV is your favorite sport.',
    check: (s) => c(s, 'murray') >= 1,
  },
  {
    id: 'donuts',
    title: 'Sick Donuts',
    line: '...you have done donuts in a trailer park at 45 mph.',
    check: (s) => c(s, 'donuts') >= 1,
  },
  {
    id: 'honest_work',
    title: "It Ain't Much",
    line: "...you mowed a man's lawn for cash and called it a career.",
    check: (s) => c(s, 'jobs') >= 3,
  },
  {
    id: 'double_wide',
    title: "Movin' On Up",
    line: '...you upgraded your home by attaching a second trailer to it.',
  },
  {
    id: 'eight_seconds',
    title: 'Eight Seconds',
    line: '...you rode a real bull for eight full seconds and called it "relaxing."',
  },
  {
    id: 'mech_bull',
    title: 'Saddle Up',
    line: '...you have ridden a mechanical bull in front of people who know your mama.',
  },
  {
    id: 'mud_king',
    title: 'Mud Bog King',
    titleW: 'Mud Bog Queen',
    line: '...your truck has more mud on it than paint, and that is on purpose.',
  },
  {
    id: 'dead_eye',
    title: 'Dead Eye',
    line: '...a row of empty cans on a fence is your idea of a fun Saturday.',
  },
  {
    id: 'arm_wrestle',
    title: 'Pinned Him',
    line: '...you have settled an argument by arm wrestling, and you were right.',
  },
  {
    id: 'bar_league',
    title: 'League Night',
    line: '...the bartender knows your darts average better than your birthday.',
  },
  {
    id: 'royal_flush',
    title: 'Royal Flush',
    line: "...your one big win in life happened on a video poker machine next to a men's room.",
  },
  {
    id: 'high_roller',
    title: 'High Roller',
    line: '...you won four figures on a scratch-off and spent it at the Booze Barn by Tuesday.',
  },
  {
    id: 'jackpot',
    title: 'MEGA BUCKS',
    line: '...you won the lottery and the first thing you bought was a bigger trailer.',
  },
  {
    id: 'new_ride',
    title: 'Fresh Off the Lot',
    line: '...you bought a vehicle from a man named Cooter and felt good about it.',
  },
  {
    id: 'rascal',
    title: 'Mobility Unlimited',
    line: '...your daily driver has a basket, a cup holder and an orange flag on a pole.',
    check: (s) => s.inv.rascal > 0,
  },
  {
    id: 'rascal_dui',
    title: 'Rascal DUI',
    line: '...you have been pulled over on a mobility scooter. Twice.',
  },
  {
    id: 'rascal_shop',
    title: 'Aisle Five Cowboy',
    line: '...you drove a scooter through the store and the clerk never even looked up.',
  },
  {
    id: 'thunder_road',
    title: 'Thunder Road',
    line: '...you have run moonshine past a deputy with a spotlight and a Waffle House napkin for a map.',
  },
  {
    id: 'busted',
    title: 'Smells Like Corn',
    line: "...a sheriff's deputy has poured out your moonshine on the gravel, real slow, while making eye contact.",
    check: (s) => c(s, 'busted') >= 1,
  },
  {
    id: 'good_neighbor',
    title: 'Good Neighbor',
    line: "...you have found a grown man's dentures in the yard and returned them for six dollars.",
    check: (s) => c(s, 'favors') >= 10,
  },
  {
    id: 'squeegee',
    title: 'Hand Done',
    line: "...you washed the neighbors' cars in the street and called it a small business.",
    check: (s) => c(s, 'carWashes') >= 10,
  },
  {
    id: 'hot_case',
    title: 'Thirty Minutes or Less',
    line: '...you have delivered gas station sushi to a man in a tinfoil bunker. And he tipped.',
    check: (s) => c(s, 'deliveries') >= 5,
  },
  {
    id: 'nosy',
    title: 'Nosy Neighbor',
    line: "...you've been inside every trailer on the block and could tell the police where everybody keeps their spare key.",
    check: (s) => NOSY_HOMES.every((id) => !!s.flags[beenInFlag(id)]),
  },
  {
    id: 'landlord',
    title: 'Landlord',
    line: "...you fixed up a burnt-out lot, rented it out, and swore you'd never be like Apex. (You are a little like Apex.)",
  },
  {
    id: 'park_owned',
    title: 'Power to the People',
    line: '...you bought the trailer park back from a private equity firm with a pickle jar.',
  },
  {
    id: 'noodler',
    title: 'Bare-Handed Fisherman',
    line: '...you have caught dinner with your arm.',
    check: (s) => c(s, 'catfish') >= 1,
  },
  {
    id: 'big_cat',
    title: 'Whiskers McGee',
    line: '...you have a photo of yourself holding a catfish bigger than your nephew.',
  },
  {
    id: 'moonshiner',
    title: 'White Lightning',
    line: '..."it\'s for fuel" is something you have said about a mason jar.',
    check: (s) => c(s, 'shineJars') >= 1,
  },
  {
    id: 'kaboom',
    title: 'Eyebrows Optional',
    line: '...you have lost your eyebrows to a still.',
  },
  {
    id: 'copperhead',
    title: 'Copperhead',
    line: '...you know the price of copper better than your own phone number.',
    check: (s) => c(s, 'copperSold') >= 20,
  },
  {
    id: 'cornhole_champ',
    title: 'Tri-County Champion',
    line: '...you have beaten a teenager at cornhole and bragged about it for a week.',
  },
  {
    id: 'lot13',
    title: 'Unlucky 13',
    line: '...you went poking around Lot 13 after dark on purpose.',
  },
  {
    id: 'couch_fire',
    title: 'Couch Potato Flambé',
    line: '...you have set a perfectly good couch on fire for fun.',
  },
  {
    id: 'fireside',
    title: 'Fireside',
    line: '...you have sat by a fire and listened to a story about a possum like it was scripture.',
    check: (s) => c(s, 'campfires') >= 1,
  },
  {
    id: 'new_do',
    title: 'New Do',
    line: '...your stylist works out of her kitchen and you would trust her with your life.',
    check: (s) => c(s, 'haircuts') >= 1,
  },
  {
    id: 'spirits',
    title: 'The Spirits Say',
    line: '...you paid a woman named Wanda ten dollars to tell you to buy a scratch-off.',
    check: (s) => c(s, 'readings') >= 1,
  },
  {
    id: 'fresh',
    title: 'Fresh to Death',
    line: '...you know which dryer at the laundromat runs hot.',
    check: (s) => c(s, 'laundry') >= 3,
  },
  {
    id: 'abducted',
    title: 'Close Encounter',
    line: '...you got abducted and your first question was about the Camaro.',
    check: (s) => c(s, 'abductions') >= 1,
  },
  {
    id: 'probed',
    title: 'Four Stars',
    line: '...you got probed by aliens and left them a review.',
  },
  {
    id: 'believer',
    title: 'Believer',
    line: '...Hank has your abduction on a cassette labeled EVIDENCE.',
  },
];

export const achievementTitle = (a: AchievementDef, woman: boolean): string => (woman && a.titleW) || a.title;

export function achievementDef(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id);
}
